import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { catchError, forkJoin, of } from 'rxjs';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { EmailChannelApiService } from '../services/email-channel-api.service';
import { ChannelConfigStatus, ChannelType, EmailChannelView } from '../models/email-channel.model';

@Component({
  selector: 'app-bulk-channel-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ModalComponent],
  template: `
    <app-modal [isOpen]="isOpen" (close)="onClose()" className="max-w-lg mx-4">
      <div class="p-6">
        <div class="mb-4">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white/90">Canal para correos masivos (bulk)</h2>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Elige por qué canal saldrán los correos masivos. Se separa del canal primario que usas para correos 1-a-1.
          </p>
        </div>

        <!-- Inc 21: guía según config-status. Si no hay canal conectado, mostramos
             el mensaje del backend (español) + CTA para conectar Gmail/Outlook/SMTP.
             Ya NO se exige fijar un canal primario antes del bulk. -->
        @if (configStatus && configStatus.missing.includes('CONNECTED_CHANNEL')) {
          <div class="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            <p>{{ configStatus.message }}</p>
            <a routerLink="/correo/canales" (click)="onClose()"
               class="mt-2 inline-block font-medium underline hover:text-amber-900 dark:hover:text-amber-200">
              Conectar Gmail, Outlook o SMTP
            </a>
          </div>
        } @else if (configStatus?.message) {
          <div class="mb-4 rounded-lg bg-gray-50 dark:bg-gray-800/60 p-3 text-xs text-gray-600 dark:text-gray-300">
            {{ configStatus?.message }}
          </div>
        }

        @if (loading) {
          <div class="space-y-2">
            @for (i of [1,2,3]; track i) {
              <div class="h-14 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800"></div>
            }
          </div>
        } @else {
          <div class="space-y-2">
            @for (ch of channels; track ch.channelType + '_' + ch.channelRefId) {
              <button type="button" (click)="select(ch)"
                      [disabled]="!ch.isConnected"
                      class="flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      [class.border-teal-300]="isSelected(ch)"
                      [class.bg-teal-50]="isSelected(ch)"
                      [class.dark:bg-teal-900/20]="isSelected(ch)"
                      [class.dark:border-teal-700]="isSelected(ch)"
                      [class.border-gray-200]="!isSelected(ch)"
                      [class.dark:border-gray-700]="!isSelected(ch)"
                      [class.hover:bg-gray-50]="!isSelected(ch) && ch.isConnected"
                      [class.dark:hover:bg-gray-800]="!isSelected(ch) && ch.isConnected">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-medium text-gray-900 dark:text-white/90">{{ ch.displayName || ch.channelType }}</span>
                    @if (ch.isPrimary) {
                      <span class="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">primario</span>
                    }
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ shortLabel(ch.channelType) }} · {{ ch.dailyRemaining }}/{{ ch.dailyLimit }} hoy</p>
                  @if (isPersonalChannel(ch.channelType) && isSelected(ch)) {
                    <p class="mt-1 text-xs text-amber-600 dark:text-amber-300">
                      ⚠️ Canal personal: límite de {{ ch.dailyLimit }}/día. Para envíos grandes, considera Resend / Mailgun / SMTP.
                    </p>
                  }
                </div>
                @if (isSelected(ch)) {
                  <svg class="h-5 w-5 flex-shrink-0 text-teal-600 dark:text-teal-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clip-rule="evenodd"></path>
                  </svg>
                }
              </button>
            }
            @if (channels.length === 0) {
              <div class="rounded-lg bg-gray-50 dark:bg-gray-800 p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                <p>No tienes canales configurados todavía.</p>
                <a routerLink="/correo/canales" (click)="onClose()"
                   class="mt-2 inline-block font-medium text-teal-600 underline hover:text-teal-700 dark:text-teal-400">
                  Conecta uno en Canales de comunicación
                </a>
              </div>
            }
          </div>
        }

        @if (errorMessage) {
          <div class="mt-3 rounded-lg bg-red-50 dark:bg-red-900/20 p-2 text-xs text-red-700 dark:text-red-300">
            {{ errorMessage }}
          </div>
        }

        <p class="mt-3 text-center text-xs text-gray-400 dark:text-gray-500">
          El canal primario es opcional. Si no eliges un canal de correos masivos, se usará tu canal primario.
          <a routerLink="/correo/canales" (click)="onClose()"
             class="font-medium text-gray-500 underline hover:text-gray-700 dark:text-gray-400">
            Configurar canal primario
          </a>
        </p>

        <div class="mt-6 flex gap-3">
          <button type="button" (click)="onClose()"
                  class="flex-1 rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
            Cancelar
          </button>
          <button type="button" (click)="save()" [disabled]="!selected || saving || !canConfigureBulk"
                  class="flex-1 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-60">
            {{ saving ? 'Guardando…' : 'Guardar canal de correos masivos' }}
          </button>
        </div>

        <div class="mt-3 text-center">
          <button type="button" (click)="clear()" [disabled]="saving"
                  class="text-xs text-gray-500 underline hover:text-gray-700 dark:hover:text-gray-300">
            Volver a usar el canal primario para correos masivos
          </button>
        </div>
      </div>
    </app-modal>
  `,
})
export class BulkChannelModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<{ channelType: ChannelType; channelRefId: number } | null>();

  private readonly channelApi = inject(EmailChannelApiService);

  channels: EmailChannelView[] = [];
  selected: EmailChannelView | null = null;
  loading = false;
  saving = false;
  errorMessage: string | null = null;
  configStatus: ChannelConfigStatus | null = null;

  /**
   * Habilita "Guardar canal de campañas". Inc 21: en cuanto hay ≥1 canal
   * conectado (`canConfigureBulk`), NO se exige un primario previo. Si el
   * endpoint no respondió aún, no bloqueamos por defecto.
   */
  get canConfigureBulk(): boolean {
    return this.configStatus ? this.configStatus.canConfigureBulk : true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.errorMessage = null;
      this.selected = null;
      this.configStatus = null;
      this.loadChannels();
    }
  }

  private loadChannels(): void {
    this.loading = true;
    forkJoin({
      channels: this.channelApi.listMine(),
      config: this.channelApi.getConfigStatus().pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ channels, config }) => {
        this.channels = channels || [];
        this.configStatus = config;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = err?.error?.message || 'No se pudieron cargar los canales.';
      },
    });
  }

  select(ch: EmailChannelView): void {
    if (!ch.isConnected) return;
    this.selected = ch;
  }

  isSelected(ch: EmailChannelView): boolean {
    return this.selected?.channelType === ch.channelType
      && this.selected?.channelRefId === ch.channelRefId;
  }

  isPersonalChannel(type: string): boolean {
    return type === 'USER_GMAIL' || type === 'USER_MICROSOFT';
  }

  shortLabel(type: string): string {
    const map: Record<string, string> = {
      USER_GMAIL: 'Gmail',
      USER_MICROSOFT: 'Outlook',
      USER_SMTP: 'SMTP',
      USER_API: 'API',
    };
    return map[type] || type;
  }

  save(): void {
    if (!this.selected) return;
    this.saving = true;
    this.errorMessage = null;
    this.channelApi.setBulk({
      bulkChannelType: this.selected.channelType as ChannelType,
      bulkChannelRefId: this.selected.channelRefId,
    }).subscribe({
      next: () => {
        this.saving = false;
        this.saved.emit({
          channelType: this.selected!.channelType as ChannelType,
          channelRefId: this.selected!.channelRefId,
        });
      },
      error: (err) => {
        this.saving = false;
        this.errorMessage = err?.error?.message || 'No se pudo guardar el canal de correos masivos.';
      },
    });
  }

  clear(): void {
    this.saving = true;
    this.errorMessage = null;
    this.channelApi.clearBulk().subscribe({
      next: () => {
        this.saving = false;
        this.saved.emit(null);
      },
      error: (err) => {
        this.saving = false;
        this.errorMessage = err?.error?.message || 'No se pudo limpiar el canal bulk.';
      },
    });
  }

  onClose(): void {
    this.close.emit();
  }
}
